/**
 * PendingKOTsReport.jsx — R4
 * Route: /pos/reports/pending-kots
 *
 * Production-ready pending KOT report.
 *
 * Important date behavior:
 * - The default report date is the hotel POS business date, not the browser/current date.
 * - Supported localStorage keys, in priority order:
 *   1. posbusiness_date
 *   2. business_date
 *   3. pos_business_date
 * - If no valid business date is found, it safely falls back to today's date.
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Table, Button, Popover, Spin, Tag, Tabs, Empty, Input } from "antd";
import {
  Download,
  Filter,
  ChevronLeft,
  ChevronRight,
  Calendar,
  RefreshCw,
  Printer,
  Search,
  Clock,
  AlertCircle,
  CheckCircle,
  XCircle,
  Coffee,
  Gift,
  RotateCcw,
  Eye,
  X,
} from "react-feather";
import moment from "moment";
import axios from "axios";
import Swal from "sweetalert2";
import SelectComponent from "react-select";
import PrintReportPreview from "../../reports/PrintReportPreview";
import PosModuleTopNav from "../../components/navigation/PosModuleTopNav";

const STATUS_COLOR = {
  pending: "orange",
  completed: "green",
  cancelled: "red",
};

const STATUS_META = {
  pending: {
    label: "Pending",
    icon: <AlertCircle size={15} />,
    className: "warning",
  },
  completed: {
    label: "Completed",
    icon: <CheckCircle size={15} />,
    className: "success",
  },
  cancelled: {
    label: "Cancelled",
    icon: <XCircle size={15} />,
    className: "danger",
  },
};

const BUSINESS_DATE_KEYS = ["posbusiness_date", "business_date", "pos_business_date"];

const getToken = () => localStorage.getItem("token");
const getCurrency = () => localStorage.getItem("currencycode") || "₹";

const getConfiguredApiBase = () =>
  (process.env.REACT_APP_API_URL || "http://localhost:3000/api").replace(/\/+$/, "");

const getPosApiBase = () => {
  const base = getConfiguredApiBase();
  return base.endsWith("/pos") ? base : `${base}/pos`;
};

const getStoredBusinessDateRaw = () => {
  for (const key of BUSINESS_DATE_KEYS) {
    const value = localStorage.getItem(key);
    if (value && moment(value).isValid()) return value;
  }

  return null;
};

const getBusinessDateMoment = () => {
  const stored = getStoredBusinessDateRaw();
  return stored ? moment(stored).startOf("day") : moment().startOf("day");
};

const getBusinessDateLabel = () => {
  const raw = getStoredBusinessDateRaw();
  return raw ? moment(raw).format("DD MMM YYYY") : "Business date not set";
};

const formatMoney = (value, currency = "₹") => {
  const n = Number(value || 0);
  return `${currency}${n.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const normalizeText = (value) => String(value || "").trim().toLowerCase();

const safeCount = (value) => Number(value || 0);

const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 42,
    borderRadius: 14,
    borderColor: state.isFocused ? "#2563eb" : "rgba(15, 23, 42, 0.12)",
    boxShadow: state.isFocused ? "0 0 0 3px rgba(37, 99, 235, 0.12)" : "none",
    fontSize: 13,
    fontWeight: 700,
    "&:hover": {
      borderColor: state.isFocused ? "#2563eb" : "rgba(15, 23, 42, 0.22)",
    },
  }),
  placeholder: (base) => ({
    ...base,
    color: "#94a3b8",
    fontWeight: 700,
  }),
  singleValue: (base) => ({
    ...base,
    color: "#0f172a",
    fontWeight: 850,
  }),
  menu: (base) => ({
    ...base,
    zIndex: 9999,
    borderRadius: 14,
    overflow: "hidden",
  }),
  option: (base, state) => ({
    ...base,
    fontSize: 13,
    fontWeight: state.isSelected ? 850 : 700,
    cursor: "pointer",
  }),
};

const downloadCSV = (headers, data, filename) => {
  const safeHeaders = headers.filter((header) => header.key);
  const headerLine = safeHeaders.map((header) => `"${String(header.label).replace(/"/g, '""')}"`).join(",");

  const rows = data.map((row) =>
    safeHeaders
      .map((header) => `"${String(row[header.key] ?? "").replace(/"/g, '""')}"`)
      .join(",")
  );

  const blob = new Blob([[headerLine, ...rows].join("\n")], {
    type: "text/csv;charset=utf-8;",
  });

  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
};

const PendingKOTsReport = () => {
  const currency = getCurrency();
  const posApiBase = getPosApiBase();
  const configuredApiBase = getConfiguredApiBase();
  const businessDate = useMemo(() => getBusinessDateMoment(), []);

  const [singleDate, setSingleDate] = useState(() => businessDate.clone());
  const [statusTab, setStatusTab] = useState("pending");
  const [loading, setLoading] = useState(false);
  const [kots, setKots] = useState([]);
  const [kotItems, setKotItems] = useState([]);
  const [statusSummary, setStatusSummary] = useState([]);
  const [filterVisible, setFilterVisible] = useState(false);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [exportData, setExportData] = useState([]);
  const [exportHeaders, setExportHeaders] = useState([]);
  const [filters, setFilters] = useState({ source: null, include_items: false });
  const [expandedKeys, setExpandedKeys] = useState([]);
  const [searchText, setSearchText] = useState("");

  // KOT preview drawer state
  const [kotPreviewKot,  setKotPreviewKot]  = useState(null);
  const [kotPreviewOpen, setKotPreviewOpen] = useState(false);
  const [kotReprinting,  setKotReprinting]  = useState(false);
  const [kotCancelling,  setKotCancelling]  = useState(null);

  const sourceOptions = useMemo(
    () => [
      { value: "table", label: "Table" },
      { value: "room", label: "Room" },
    ],
    []
  );

  const itemsByKot = useMemo(() => {
    return (kotItems || []).reduce((acc, item) => {
      const key = item.kot_id;
      if (!acc[key]) acc[key] = [];
      acc[key].push(item);
      return acc;
    }, {});
  }, [kotItems]);

  const statusCount = useCallback(
    (status) => {
      const found = (statusSummary || []).find(
        (row) => normalizeText(row.status) === normalizeText(status)
      );

      return safeCount(found?.count);
    },
    [statusSummary]
  );

  const fetchReport = useCallback(async () => {
    const token = getToken();

    if (!token) {
      Swal.fire({
        icon: "error",
        title: "Authentication required",
        text: "Login token is missing. Please login again.",
      });
      return;
    }

    setLoading(true);

    try {
      const params = {
        business_date: singleDate.format("YYYY-MM-DD"),
        status: statusTab,
        include_items: filters.include_items ? "true" : "false",
        ...(filters.source ? { source: filters.source.value } : {}),
      };

      const response = await axios.get(`${posApiBase}/pending-kots`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });

      if (response.data?.success) {
        setKots(response.data.kots || []);
        setKotItems(response.data.kotItems || []);
        setStatusSummary(response.data.statusSummary || []);
        setExpandedKeys([]);
      } else {
        setKots([]);
        setKotItems([]);
        setStatusSummary([]);
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: err?.response?.data?.error || err.message || "Failed to load Pending KOTs report.",
      });
    } finally {
      setLoading(false);
    }
  }, [singleDate, statusTab, filters, posApiBase]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const filteredKots = useMemo(() => {
    const q = normalizeText(searchText);

    if (!q) return kots;

    return (kots || []).filter((kot) => {
      return [
        kot.kot_number,
        kot.table_room,
        kot.source_type,
        kot.status,
        kot.waiter_name,
        kot.raised_by_user_id,
      ]
        .map(normalizeText)
        .some((value) => value.includes(q));
    });
  }, [kots, searchText]);

  const reportStats = useMemo(() => {
    const rows = filteredKots || [];
    const totalKots = rows.length;
    const totalItems = rows.reduce((sum, row) => sum + safeCount(row.item_count), 0);
    const complementary = rows.filter((row) => row.is_complementary).length;
    const aged = rows.filter((row) => safeCount(row.age_minutes) > 30).length;
    const averageAge =
      totalKots > 0
        ? rows.reduce((sum, row) => sum + safeCount(row.age_minutes), 0) / totalKots
        : 0;

    return {
      totalKots,
      totalItems,
      complementary,
      aged,
      averageAge,
    };
  }, [filteredKots]);

  const changeDate = (direction) => {
    setSingleDate((previous) =>
      previous.clone().add(direction === "next" ? 1 : -1, "day").startOf("day")
    );
  };

  const resetToBusinessDate = () => {
    setSingleDate(getBusinessDateMoment());
  };

  const exportRows = useMemo(() => {
    return (filteredKots || []).map((row) => ({
      kot_number: row.kot_number || "",
      created_at: row.created_at ? moment(row.created_at).format("DD MMM YYYY, hh:mm A") : "",
      age_minutes: safeCount(row.age_minutes),
      table_room: row.table_room || "",
      source_type: row.source_type || "",
      status: row.status || "",
      item_count: safeCount(row.item_count),
      is_complementary: row.is_complementary ? "Yes" : "No",
      waiter_name: row.waiter_name || "",
      raised_by_user_id: row.raised_by_user_id || "",
    }));
  }, [filteredKots]);

  const exportHeaderList = useMemo(
    () => [
      { label: "KOT No", key: "kot_number" },
      { label: "Time", key: "created_at" },
      { label: "Age (min)", key: "age_minutes" },
      { label: "Table/Room", key: "table_room" },
      { label: "Source", key: "source_type" },
      { label: "Status", key: "status" },
      { label: "Items", key: "item_count" },
      { label: "Complementary", key: "is_complementary" },
      { label: "Waiter", key: "waiter_name" },
      { label: "Raised By", key: "raised_by_user_id" },
    ],
    []
  );

  const handleViewKot = async (record) => {
    const existing = itemsByKot[record.kot_id] || [];
    if (existing.length > 0) {
      setKotPreviewKot({ row: record, items: existing });
      setKotPreviewOpen(true);
      return;
    }
    try {
      const token = getToken();
      const res = await axios.get(`${posApiBase}/pending-kots`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { business_date: singleDate.format("YYYY-MM-DD"), status: "all", include_items: "true", kot_id: record.kot_id },
      });
      const fetched = (res.data?.kotItems || []).filter(i => i.kot_id === record.kot_id);
      setKotPreviewKot({ row: record, items: fetched });
    } catch { setKotPreviewKot({ row: record, items: [] }); }
    setKotPreviewOpen(true);
  };

  const handleReprintKot = async (record) => {
    setKotReprinting(true);
    try {
      const token = getToken();
      const base = posApiBase.replace(/\/reports\/pending-kots$/, "").replace(/\/pos\/reports$/, "/pos");
      await axios.patch(`${base}/kots/${record.kot_id}/reprint`, {}, { headers: { Authorization: `Bearer ${token}` } });
      Swal.fire({ icon: "success", title: "Reprint sent", text: `${record.kot_number} sent to kitchen printer.`, timer: 2000, showConfirmButton: false });
    } catch (err) {
      Swal.fire({ icon: "error", title: "Reprint failed", text: err?.response?.data?.error || err.message });
    } finally { setKotReprinting(false); }
  };

  const handleCancelKot = async (record) => {
    const result = await Swal.fire({
      title: `Cancel ${record.kot_number}?`,
      html: `<p>Table/Room: <strong>${record.table_room || "—"}</strong><br>${record.item_count || 0} item(s). This cannot be undone.</p>`,
      icon: "warning", showCancelButton: true,
      confirmButtonText: "Yes, cancel KOT", cancelButtonText: "Keep it", confirmButtonColor: "#dc2626",
    });
    if (!result.isConfirmed) return;
    setKotCancelling(record.kot_id);
    try {
      const token = getToken();
      const base = posApiBase.replace(/\/reports\/pending-kots$/, "").replace(/\/pos\/reports$/, "/pos");
      await axios.patch(`${base}/kots/${record.kot_id}/cancel`, {}, { headers: { Authorization: `Bearer ${token}` } });
      Swal.fire({ icon: "success", title: "KOT cancelled", timer: 1800, showConfirmButton: false });
      fetchReport();
      if (kotPreviewKot?.row?.kot_id === record.kot_id) setKotPreviewOpen(false);
    } catch (err) {
      Swal.fire({ icon: "error", title: "Cancel failed", text: err?.response?.data?.error || err.message });
    } finally { setKotCancelling(null); }
  };

  const columns = useMemo(
    () => [
      {
        title: "KOT No",
        dataIndex: "kot_number",
        width: 160,
        fixed: "left",
        render: (value, record) => (
          <div className="ns-pkot-kot-cell">
            <strong>{value || "-"}</strong>
            {record.is_complementary ? (
              <span className="ns-pkot-mini-badge comp">
                <Gift size={12} />
                Comp
              </span>
            ) : null}
          </div>
        ),
      },
      {
        title: "Time",
        dataIndex: "created_at",
        width: 170,
        render: (value) => (
          <span className="ns-pkot-time">
            {value ? moment(value).format("DD MMM, hh:mm A") : "—"}
          </span>
        ),
      },
      {
        title: "Age",
        dataIndex: "age_minutes",
        width: 105,
        align: "right",
        render: (value) => {
          const n = Number(value || 0);
          const className = n > 30 ? "danger" : n > 15 ? "warning" : "success";

          return <span className={`ns-pkot-age ${className}`}>{n} min</span>;
        },
      },
      {
        title: "Table/Room",
        dataIndex: "table_room",
        width: 130,
        render: (value) => <span className="ns-pkot-main-text">{value || "—"}</span>,
      },
      {
        title: "Source",
        dataIndex: "source_type",
        width: 105,
        render: (value) => <span className="ns-pkot-soft-pill">{value || "—"}</span>,
      },
      {
        title: "Status",
        dataIndex: "status",
        width: 120,
        render: (value) => (
          <Tag color={STATUS_COLOR[normalizeText(value)] || "default"}>
            {value || "—"}
          </Tag>
        ),
      },
      {
        title: "Items",
        dataIndex: "item_count",
        width: 80,
        align: "right",
        render: (value) => <span className="ns-pkot-count">{safeCount(value)}</span>,
      },
      {
        title: "Comp",
        dataIndex: "is_complementary",
        width: 80,
        align: "center",
        render: (value) =>
          value ? <span className="ns-pkot-mini-badge comp">Yes</span> : <span className="ns-pkot-muted">—</span>,
      },
      {
        title: "Waiter",
        dataIndex: "waiter_name",
        width: 140,
        render: (value) => value || <span className="ns-pkot-muted">—</span>,
      },
      {
        title: "Raised By",
        dataIndex: "raised_by_user_id",
        width: 120,
        render: (value) => value || <span className="ns-pkot-muted">—</span>,
      },
      {
        title: "Actions",
        key: "actions",
        fixed: "right",
        width: 195,
        render: (_, record) => {
          const isPending    = normalizeText(record.status) === "pending";
          const isCancelling = kotCancelling === record.kot_id;
          const pill = (onClick, color, bg, border, disabled, icon, label) => (
            <button onClick={onClick} disabled={disabled} style={{
              display: "inline-flex", alignItems: "center", gap: 4,
              height: 28, borderRadius: 9, border: `1px solid ${border}`,
              background: bg, color, fontSize: "0.72rem", fontWeight: 950,
              padding: "0 9px", cursor: disabled ? "not-allowed" : "pointer",
              opacity: disabled ? 0.7 : 1,
            }}>
              {icon} {label}
            </button>
          );
          return (
            <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
              {pill(() => handleViewKot(record), "#1d4ed8", "#eff6ff", "#bfdbfe", false, <Eye size={12}/>, "View")}
              {(isPending || normalizeText(record.status) === "completed") &&
                pill(() => handleReprintKot(record), "#166534", "#f0fdf4", "#bbf7d0", kotReprinting, <Printer size={12}/>, "Re-print")}
              {isPending &&
                pill(() => handleCancelKot(record), "#dc2626", "#fef2f2", "#fecaca", !!isCancelling, <XCircle size={12}/>, isCancelling ? "..." : "Cancel")}
            </div>
          );
        },
      },
    ],
    [kotCancelling, kotReprinting]
  );

  const itemSubColumns = useMemo(
    () => [
      {
        title: "Item",
        dataIndex: "item_name",
        width: 220,
        render: (value) => <span className="ns-pkot-main-text">{value || "—"}</span>,
      },
      {
        title: "Qty",
        dataIndex: "qty",
        width: 80,
        align: "right",
        render: (value) => safeCount(value),
      },
      {
        title: `Price (${currency})`,
        dataIndex: "unit_price",
        width: 120,
        align: "right",
        render: (value) => Number(value || 0).toFixed(2),
      },
      {
        title: `Total (${currency})`,
        dataIndex: "line_total",
        width: 120,
        align: "right",
        render: (value) => <span className="ns-pkot-main-text">{Number(value || 0).toFixed(2)}</span>,
      },
      {
        title: "Comp",
        dataIndex: "is_complementary",
        width: 80,
        align: "center",
        render: (value) => (value ? <Tag color="blue">Yes</Tag> : null),
      },
    ],
    [currency]
  );

  const expandable = filters.include_items
    ? {
        expandedRowKeys: expandedKeys,
        onExpand: (expanded, record) => {
          setExpandedKeys(expanded ? [record.kot_id] : expandedKeys.filter((key) => key !== record.kot_id));
        },
        expandedRowRender: (record) => {
          const items = itemsByKot[record.kot_id] || [];

          return items.length > 0 ? (
            <div className="ns-pkot-expanded-row">
              <Table
                columns={itemSubColumns}
                dataSource={items}
                rowKey={(row, index) => `${record.kot_id}-${row.item_id || row.item_name || index}`}
                size="small"
                pagination={false}
              />
            </div>
          ) : (
            <p className="ns-pkot-expanded-empty">No item detail loaded.</p>
          );
        },
      }
    : undefined;

  const applyFiltersAndClose = () => {
    setFilterVisible(false);
    fetchReport();
  };

  const clearFilters = () => {
    setFilters({ source: null, include_items: false });
    setSearchText("");
  };

  const filterContent = (
    <div className="ns-pkot-filter-panel">
      <div>
        <label>Source</label>
        <SelectComponent
          isClearable
          options={sourceOptions}
          value={filters.source}
          onChange={(value) => setFilters((previous) => ({ ...previous, source: value }))}
          styles={selectStyles}
          placeholder="Table / Room"
          menuPortalTarget={typeof document !== "undefined" ? document.body : null}
          menuPosition="fixed"
        />
      </div>

      <label className="ns-pkot-check-row">
        <input
          type="checkbox"
          checked={filters.include_items}
          onChange={(event) =>
            setFilters((previous) => ({
              ...previous,
              include_items: event.target.checked,
            }))
          }
        />
        <span>Load item detail for expandable rows</span>
      </label>

      <div className="ns-pkot-filter-actions">
        <Button onClick={clearFilters}>Clear</Button>
        <Button type="primary" onClick={applyFiltersAndClose}>
          Apply
        </Button>
      </div>
    </div>
  );

  const footerData = useMemo(
    () => [
      { key: "Total KOTs", value: reportStats.totalKots },
      { key: "Total Items", value: reportStats.totalItems },
      { key: "Avg Age", value: `${reportStats.averageAge.toFixed(1)} min` },
      { key: "Aged > 30 min", value: reportStats.aged },
      { key: "Complementary", value: reportStats.complementary },
    ],
    [reportStats]
  );


  const KotPreviewDrawer = () => {
    if (!kotPreviewOpen || !kotPreviewKot) return null;
    const { row, items } = kotPreviewKot;
    const isPending   = normalizeText(row.status) === "pending";
    const isCompleted = normalizeText(row.status) === "completed";

    return (
      <>
        <div onClick={() => setKotPreviewOpen(false)} style={{ position:"fixed",inset:0,zIndex:2000,background:"rgba(15,23,42,0.38)",animation:"nsBdIn 0.2s ease both" }} />
        <div style={{ position:"fixed",top:0,right:0,bottom:0,width:"min(480px,100vw)",background:"#f8fafc",boxShadow:"-28px 0 70px rgba(15,23,42,0.22)",zIndex:2001,display:"flex",flexDirection:"column",animation:"nsDrawIn 0.26s cubic-bezier(0.22,1,0.36,1) both" }}>

          <div style={{ background:"linear-gradient(135deg,#1d4ed8,#2563eb)",padding:"14px 18px",flexShrink:0,display:"flex",alignItems:"center",justifyContent:"space-between",color:"#fff" }}>
            <div>
              <div style={{ fontSize:"0.68rem",textTransform:"uppercase",letterSpacing:"0.12em",opacity:0.8 }}>KOT Preview</div>
              <div style={{ fontWeight:950,fontSize:"1.05rem" }}>{row.kot_number}</div>
              <div style={{ fontSize:"0.76rem",opacity:0.8,marginTop:2 }}>{row.table_room?`${row.source_type||"Table"}: ${row.table_room} · `:""}{row.status}</div>
            </div>
            <div style={{ display:"flex",gap:8 }}>
              {(isPending||isCompleted) && (
                <button onClick={() => handleReprintKot(row)} disabled={kotReprinting}
                  style={{ display:"inline-flex",alignItems:"center",gap:6,height:36,borderRadius:12,background:kotReprinting?"rgba(255,255,255,0.2)":"#fff",color:kotReprinting?"#fff":"#1d4ed8",border:0,padding:"0 13px",fontWeight:950,fontSize:"0.8rem",cursor:kotReprinting?"not-allowed":"pointer" }}>
                  <Printer size={14}/> {kotReprinting?"Sending...":"Re-print"}
                </button>
              )}
              <button onClick={() => setKotPreviewOpen(false)}
                style={{ width:36,height:36,borderRadius:12,background:"rgba(255,255,255,0.15)",border:0,color:"#fff",display:"inline-flex",alignItems:"center",justifyContent:"center",cursor:"pointer" }}>
                <X size={17}/>
              </button>
            </div>
          </div>

          <div style={{ flex:1,overflowY:"auto",padding:"20px 16px",display:"flex",justifyContent:"center" }}>
            <div style={{ width:300,fontFamily:"'Courier New',monospace",fontSize:12,background:"#fff",border:"1px dashed #d1d5db",borderRadius:8,padding:"16px 14px",boxShadow:"0 10px 30px rgba(15,23,42,0.10)" }}>

              <div style={{ textAlign:"center",marginBottom:8 }}>
                <div style={{ fontWeight:700,fontSize:15 }}>KITCHEN ORDER</div>
                {row.is_complementary && (
                  <div style={{ display:"inline-block",background:"#eff6ff",color:"#1d4ed8",border:"1px solid #bfdbfe",borderRadius:6,padding:"2px 10px",fontSize:10,fontWeight:700,marginTop:3 }}>
                    ★ COMPLIMENTARY ★
                  </div>
                )}
              </div>
              <div style={{ borderTop:"1px dashed #ccc",margin:"8px 0" }} />

              <div style={{ fontSize:11,marginBottom:6 }}>
                {[
                  ["KOT #", row.kot_number],
                  row.table_room ? [row.source_type==="Room"?"Room  ":"Table ", row.table_room] : null,
                  ["Time  ", row.created_at ? moment(row.created_at).format("HH:mm, DD MMM") : "—"],
                  row.waiter_name ? ["Waiter", row.waiter_name] : null,
                ].filter(Boolean).map(([label, val]) => (
                  <div key={label} style={{ display:"flex",justifyContent:"space-between" }}>
                    <span style={{ color:"#555" }}>{label}</span>
                    <span style={{ fontWeight:700 }}>{val}</span>
                  </div>
                ))}
              </div>
              <div style={{ borderTop:"1px dashed #ccc",margin:"8px 0" }} />

              {items.length > 0 ? items.map((item, i) => (
                <div key={i} style={{ marginBottom:6 }}>
                  <div style={{ fontWeight:700,fontSize:13 }}>{item.qty}x  {item.item_name||item.name||"Unknown item"}</div>
                  {item.unit_price > 0 && (
                    <div style={{ color:"#64748b",fontSize:11,textAlign:"right" }}>
                      {item.qty} × {Number(item.unit_price||0).toFixed(2)} = {Number(item.line_total||0).toFixed(2)}
                    </div>
                  )}
                  {item.notes && <div style={{ color:"#94a3b8",fontSize:10,paddingLeft:8 }}>› {item.notes}</div>}
                </div>
              )) : (
                <div style={{ textAlign:"center",color:"#94a3b8",fontSize:11,padding:"12px 0",fontStyle:"italic" }}>
                  {row.item_count > 0
                    ? `${row.item_count} item(s) · enable "Load item detail" in filters to see breakdown`
                    : "No items"}
                </div>
              )}
              <div style={{ borderTop:"1px dashed #ccc",margin:"8px 0" }} />
              <div style={{ textAlign:"center",fontSize:10,color:"#94a3b8" }}>
                <div>Age: {row.age_minutes||0} min{(row.age_minutes||0)>30?" ⚠ AGED":""}</div>
                <div style={{ marginTop:4 }}>Status: {(row.status||"").toUpperCase()}</div>
              </div>
            </div>
          </div>

          <div style={{ flexShrink:0,background:"#fff",borderTop:"1px solid rgba(15,23,42,0.08)",padding:"12px 16px",display:"flex",gap:8 }}>
            {(isPending||isCompleted) && (
              <button onClick={() => handleReprintKot(row)} disabled={kotReprinting}
                style={{ flex:1,height:44,borderRadius:13,border:0,background:kotReprinting?"#94a3b8":"linear-gradient(135deg,#1d4ed8,#2563eb)",color:"#fff",fontWeight:950,fontSize:"0.88rem",display:"inline-flex",alignItems:"center",justifyContent:"center",gap:7,cursor:kotReprinting?"not-allowed":"pointer" }}>
                <Printer size={16}/> {kotReprinting?"Sending...":"Print from Preview"}
              </button>
            )}
            {isPending && (
              <button onClick={() => handleCancelKot(row)} disabled={!!kotCancelling}
                style={{ height:44,borderRadius:13,border:"1px solid #fecaca",background:"#fef2f2",color:"#dc2626",fontWeight:950,padding:"0 16px",cursor:kotCancelling?"not-allowed":"pointer",display:"inline-flex",alignItems:"center",gap:6,fontSize:"0.88rem" }}>
                <XCircle size={15}/> Cancel KOT
              </button>
            )}
            <button onClick={() => setKotPreviewOpen(false)}
              style={{ height:44,borderRadius:13,border:"1px solid rgba(15,23,42,0.10)",background:"#f8fafc",color:"#475569",fontWeight:950,padding:"0 16px",cursor:"pointer" }}>
              Close
            </button>
          </div>
        </div>
        <style>{"@keyframes nsBdIn{from{opacity:0}to{opacity:1}} @keyframes nsDrawIn{from{transform:translateX(100%)}to{transform:translateX(0)}}"}</style>
      </>
    );
  };

  return (
    <div className="page-wrapper">
      <style>{`
        .ns-pkot-page {
          min-height: calc(100vh - 70px);
          background:
            radial-gradient(circle at 10% 4%, rgba(37, 99, 235, 0.10), transparent 28%),
            radial-gradient(circle at 86% 0%, rgba(245, 158, 11, 0.12), transparent 26%),
            #f8fafc;
          padding-bottom: 26px;
        }

        .ns-pkot-hero {
          position: relative;
          overflow: hidden;
          background:
            radial-gradient(circle at top left, rgba(37, 99, 235, 0.18), transparent 34%),
            radial-gradient(circle at top right, rgba(245, 158, 11, 0.12), transparent 30%),
            linear-gradient(135deg, #ffffff 0%, #f8fbff 100%);
          border: 1px solid rgba(15, 23, 42, 0.08);
          border-radius: 26px;
          box-shadow: 0 20px 52px rgba(15, 23, 42, 0.08);
          padding: 16px 18px;
          margin-bottom: 12px;
        }

        .ns-pkot-hero-inner {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: 18px;
          flex-wrap: wrap;
        }

        .ns-pkot-title-wrap {
          display: flex;
          align-items: flex-start;
          gap: 13px;
          min-width: 0;
        }

        .ns-pkot-title-icon {
          width: 48px;
          height: 48px;
          border-radius: 18px;
          background: linear-gradient(135deg, #1d4ed8, #38bdf8);
          color: #ffffff;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 14px 30px rgba(37, 99, 235, 0.24);
          flex: 0 0 auto;
        }

        .ns-pkot-eyebrow {
          color: #2563eb;
          font-size: 0.72rem;
          font-weight: 950;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          margin-bottom: 5px;
        }

        .ns-pkot-title {
          color: #001f5b;
          font-size: 1.5rem;
          line-height: 1.16;
          font-weight: 950;
          margin: 0;
        }

        .ns-pkot-subtitle {
          color: #64748b;
          font-size: 0.88rem;
          line-height: 1.55;
          margin: 7px 0 0;
          max-width: 780px;
        }


        .ns-pkot-top-right {
          display: grid;
          gap: 8px;
          justify-items: end;
          min-width: min(460px, 100%);
        }

        .ns-pkot-business-chip {
          min-height: 30px;
          border-radius: 999px;
          background: #ffffff;
          border: 1px solid rgba(15, 23, 42, 0.08);
          color: #64748b;
          padding: 5px 10px;
          display: inline-flex;
          align-items: center;
          gap: 7px;
          box-shadow: 0 8px 20px rgba(15, 23, 42, 0.04);
          font-size: 0.72rem;
          font-weight: 900;
          white-space: nowrap;
        }

        .ns-pkot-business-chip strong {
          color: #0f172a;
        }

        .ns-pkot-compact-date-switcher {
          display: inline-flex;
          align-items: center;
          gap: 7px;
          background: rgba(255, 255, 255, 0.95);
          border: 1px solid rgba(15, 23, 42, 0.08);
          border-radius: 18px;
          box-shadow: 0 14px 34px rgba(15, 23, 42, 0.08);
          padding: 7px;
        }

        .ns-pkot-reset-btn {
          height: 40px !important;
          border-radius: 14px !important;
          display: inline-flex !important;
          align-items: center !important;
          justify-content: center !important;
          gap: 6px !important;
          font-weight: 900 !important;
        }

        .ns-pkot-date-console {
          background: rgba(255, 255, 255, 0.94);
          border: 1px solid rgba(15, 23, 42, 0.08);
          border-radius: 20px;
          box-shadow: 0 14px 34px rgba(15, 23, 42, 0.08);
          padding: 12px;
          min-width: min(360px, 100%);
        }

        .ns-pkot-date-label {
          color: #64748b;
          font-size: 0.72rem;
          font-weight: 900;
          margin-bottom: 8px;
          display: flex;
          justify-content: space-between;
          gap: 8px;
          flex-wrap: wrap;
        }

        .ns-pkot-date-label strong {
          color: #0f172a;
        }

        .ns-pkot-date-control {
          display: grid;
          grid-template-columns: 42px minmax(0, 1fr) 42px;
          gap: 8px;
          align-items: center;
        }

        .ns-pkot-date-btn,
        .ns-pkot-icon-btn {
          height: 40px !important;
          border-radius: 14px !important;
          display: inline-flex !important;
          align-items: center !important;
          justify-content: center !important;
          gap: 7px !important;
          font-weight: 900 !important;
        }

        .ns-pkot-current-date {
          height: 40px;
          border-radius: 14px;
          background: #f8fafc;
          border: 1px solid rgba(15, 23, 42, 0.08);
          color: #0f172a;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          font-size: 0.9rem;
          font-weight: 950;
          padding: 0 12px;
          white-space: nowrap;
        }

        .ns-pkot-business-reset {
          margin-top: 8px;
          width: 100%;
          border-radius: 13px !important;
          font-weight: 900 !important;
          height: 36px !important;
        }

        .ns-pkot-kpi-grid {
          display: grid;
          grid-template-columns: repeat(5, minmax(0, 1fr));
          gap: 10px;
          margin-bottom: 12px;
        }

        .ns-pkot-kpi-card {
          background: #ffffff;
          border: 1px solid rgba(15, 23, 42, 0.08);
          border-radius: 18px;
          box-shadow: 0 10px 28px rgba(15, 23, 42, 0.06);
          padding: 13px;
          min-width: 0;
        }

        .ns-pkot-kpi-card span {
          display: block;
          color: #64748b;
          font-size: 0.68rem;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          font-weight: 950;
          margin-bottom: 5px;
        }

        .ns-pkot-kpi-card strong {
          display: block;
          color: #0f172a;
          font-size: 1.16rem;
          font-weight: 950;
          line-height: 1.1;
        }

        .ns-pkot-kpi-card small {
          color: #94a3b8;
          font-size: 0.72rem;
          display: block;
          margin-top: 5px;
        }

        .ns-pkot-kpi-card.warning { border-top: 3px solid #f59e0b; }
        .ns-pkot-kpi-card.success { border-top: 3px solid #22c55e; }
        .ns-pkot-kpi-card.danger { border-top: 3px solid #ef4444; }
        .ns-pkot-kpi-card.blue { border-top: 3px solid #2563eb; }
        .ns-pkot-kpi-card.slate { border-top: 3px solid #64748b; }

        .ns-pkot-shell {
          background: rgba(255, 255, 255, 0.98);
          border: 1px solid rgba(15, 23, 42, 0.08);
          border-radius: 24px;
          box-shadow: 0 18px 48px rgba(15, 23, 42, 0.08);
          overflow: hidden;
        }

        .ns-pkot-toolbar {
          padding: 14px 16px;
          border-bottom: 1px solid rgba(15, 23, 42, 0.08);
          display: flex;
          justify-content: space-between;
          gap: 12px;
          flex-wrap: wrap;
          align-items: center;
          background: #ffffff;
        }

        .ns-pkot-search {
          position: relative;
          flex: 1 1 340px;
          max-width: 520px;
        }

        .ns-pkot-search svg {
          position: absolute;
          left: 13px;
          top: 50%;
          transform: translateY(-50%);
          color: #94a3b8;
          pointer-events: none;
        }

        .ns-pkot-search input {
          width: 100%;
          height: 42px;
          border-radius: 14px;
          border: 1px solid rgba(15, 23, 42, 0.12);
          background: #ffffff;
          padding: 0 42px 0 40px;
          color: #0f172a;
          font-size: 0.84rem;
          font-weight: 780;
          outline: none;
          box-shadow: 0 6px 14px rgba(15, 23, 42, 0.04);
          transition: border-color 0.16s ease, box-shadow 0.16s ease;
        }

        .ns-pkot-search input:focus {
          border-color: #2563eb;
          box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.12);
        }

        .ns-pkot-search button {
          position: absolute;
          right: 7px;
          top: 50%;
          transform: translateY(-50%);
          width: 28px;
          height: 28px;
          border-radius: 10px;
          border: 0;
          background: #f1f5f9;
          color: #64748b;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
        }

        .ns-pkot-toolbar-actions {
          display: flex;
          align-items: center;
          gap: 8px;
          flex-wrap: wrap;
        }

        .ns-pkot-tabs {
          padding: 0 16px 16px;
        }

        .ns-pkot-tabs .ant-tabs-nav {
          margin-bottom: 14px !important;
        }

        .ns-pkot-tab-title {
          display: inline-flex;
          align-items: center;
          gap: 7px;
          font-weight: 950;
        }

        .ns-pkot-tab-count {
          min-width: 23px;
          height: 22px;
          border-radius: 999px;
          background: #eff6ff;
          color: #1d4ed8;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 0 8px;
          font-size: 0.72rem;
          font-weight: 950;
        }

        .ns-pkot-table .ant-table {
          border-radius: 18px !important;
          overflow: hidden;
          border: 1px solid rgba(15, 23, 42, 0.08);
        }

        .ns-pkot-table .ant-table-thead > tr > th {
          background: #f8fafc !important;
          color: #0f172a !important;
          font-weight: 950 !important;
          font-size: 0.78rem;
          text-transform: uppercase;
          letter-spacing: 0.03em;
        }

        .ns-pkot-table .ant-table-tbody > tr:hover > td {
          background: #f8fbff !important;
        }

        .ns-pkot-kot-cell strong,
        .ns-pkot-main-text {
          color: #0f172a;
          font-weight: 900;
        }

        .ns-pkot-kot-cell {
          display: flex;
          align-items: center;
          gap: 7px;
          flex-wrap: wrap;
        }

        .ns-pkot-time,
        .ns-pkot-muted {
          color: #94a3b8;
          font-weight: 800;
        }

        .ns-pkot-soft-pill,
        .ns-pkot-mini-badge,
        .ns-pkot-age,
        .ns-pkot-count {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          border-radius: 999px;
          font-size: 0.74rem;
          font-weight: 950;
          white-space: nowrap;
        }

        .ns-pkot-soft-pill {
          background: #f8fafc;
          color: #475569;
          border: 1px solid rgba(15, 23, 42, 0.08);
          padding: 5px 9px;
        }

        .ns-pkot-mini-badge {
          gap: 4px;
          padding: 4px 8px;
        }

        .ns-pkot-mini-badge.comp {
          background: #eff6ff;
          color: #1d4ed8;
          border: 1px solid #bfdbfe;
        }

        .ns-pkot-age {
          padding: 5px 9px;
        }

        .ns-pkot-age.success {
          background: #ecfdf5;
          color: #047857;
          border: 1px solid #bbf7d0;
        }

        .ns-pkot-age.warning {
          background: #fffbeb;
          color: #b45309;
          border: 1px solid #fde68a;
        }

        .ns-pkot-age.danger {
          background: #fef2f2;
          color: #b91c1c;
          border: 1px solid #fecaca;
        }

        .ns-pkot-count {
          min-width: 30px;
          height: 26px;
          background: #f8fafc;
          color: #0f172a;
          border: 1px solid rgba(15, 23, 42, 0.08);
        }

        .ns-pkot-expanded-row {
          background: #f8fafc;
          border-radius: 16px;
          padding: 10px;
        }

        .ns-pkot-expanded-empty {
          padding: 10px;
          color: #94a3b8;
          margin: 0;
          font-weight: 800;
        }

        .ns-pkot-filter-panel {
          width: 280px;
          display: grid;
          gap: 13px;
        }

        .ns-pkot-filter-panel label {
          color: #0f172a;
          font-size: 0.78rem;
          font-weight: 900;
          display: block;
          margin-bottom: 5px;
        }

        .ns-pkot-check-row {
          display: flex !important;
          align-items: center;
          gap: 8px;
          cursor: pointer;
          margin: 0 !important;
        }

        .ns-pkot-check-row input {
          width: 16px;
          height: 16px;
        }

        .ns-pkot-filter-actions {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 8px;
        }

        .ns-pkot-loader {
          min-height: 420px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .ns-pkot-loader-card {
          background: #ffffff;
          border: 1px solid rgba(15, 23, 42, 0.08);
          border-radius: 22px;
          box-shadow: 0 18px 48px rgba(15, 23, 42, 0.10);
          padding: 26px 32px;
          display: inline-flex;
          gap: 14px;
          align-items: center;
          color: #64748b;
          font-weight: 900;
        }


        .ns-pkot-hero {
          padding: 16px 18px !important;
        }

        .ns-pkot-hero-inner {
          display: grid !important;
          grid-template-columns: minmax(260px, 0.75fr) minmax(520px, 1.55fr);
          align-items: start !important;
          gap: 14px !important;
          flex-wrap: unset !important;
        }

        .ns-pkot-title-wrap {
          min-width: 0;
        }

        .ns-pkot-title {
          margin-bottom: 0 !important;
        }

        .ns-pkot-subtitle {
          display: none !important;
        }

        .ns-pkot-top-right {
          min-width: 0 !important;
          width: 100%;
          display: grid !important;
          gap: 8px !important;
          justify-items: end !important;
        }

        .ns-pkot-quicknav-inline {
          min-width: 0;
          width: 100%;
          display: flex;
          justify-content: flex-end;
        }

        .ns-pkot-business-chip {
          justify-self: end;
        }

        .ns-pkot-compact-date-switcher {
          justify-self: end;
        }

        @media (max-width: 1280px) {
          .ns-pkot-hero-inner {
            grid-template-columns: 1fr !important;
          }

          .ns-pkot-top-right {
            justify-items: stretch !important;
          }

          .ns-pkot-quicknav-inline {
            justify-content: flex-start;
          }

          .ns-pkot-business-chip,
          .ns-pkot-compact-date-switcher {
            justify-self: start;
          }
        }

        @media (max-width: 1320px) {
          .ns-pkot-kpi-grid {
            grid-template-columns: repeat(3, minmax(0, 1fr));
          }
        }

        @media (max-width: 860px) {
          .ns-pkot-hero {
            padding: 16px;
            border-radius: 22px;
          }

          .ns-pkot-title {
            font-size: 1.28rem;
          }

  
        .ns-pkot-top-right {
          display: grid;
          gap: 8px;
          justify-items: end;
          min-width: min(460px, 100%);
        }

        .ns-pkot-business-chip {
          min-height: 30px;
          border-radius: 999px;
          background: #ffffff;
          border: 1px solid rgba(15, 23, 42, 0.08);
          color: #64748b;
          padding: 5px 10px;
          display: inline-flex;
          align-items: center;
          gap: 7px;
          box-shadow: 0 8px 20px rgba(15, 23, 42, 0.04);
          font-size: 0.72rem;
          font-weight: 900;
          white-space: nowrap;
        }

        .ns-pkot-business-chip strong {
          color: #0f172a;
        }

        .ns-pkot-compact-date-switcher {
          display: inline-flex;
          align-items: center;
          gap: 7px;
          background: rgba(255, 255, 255, 0.95);
          border: 1px solid rgba(15, 23, 42, 0.08);
          border-radius: 18px;
          box-shadow: 0 14px 34px rgba(15, 23, 42, 0.08);
          padding: 7px;
        }

        .ns-pkot-reset-btn {
          height: 40px !important;
          border-radius: 14px !important;
          display: inline-flex !important;
          align-items: center !important;
          justify-content: center !important;
          gap: 6px !important;
          font-weight: 900 !important;
        }

        .ns-pkot-date-console {
            width: 100%;
          }

          .ns-pkot-kpi-grid {
            grid-template-columns: repeat(2, minmax(0, 1fr));
          }

          .ns-pkot-search {
            flex-basis: 100%;
            max-width: 100%;
          }
        }

        @media (max-width: 560px) {
          .ns-pkot-kpi-grid {
            grid-template-columns: 1fr;
          }

          .ns-pkot-date-control {
            grid-template-columns: 38px minmax(0, 1fr) 38px;
          }

          .ns-pkot-current-date {
            font-size: 0.8rem;
          }
        }
      `}</style>

      <div className="content container-fluid ns-pkot-page">
        <section className="ns-pkot-hero">
          <div className="ns-pkot-hero-inner">
            <div className="ns-pkot-title-wrap">
              <span className="ns-pkot-title-icon">
                <Coffee size={24} />
              </span>

              <div>
                <div className="ns-pkot-eyebrow">Kitchen Operations Report</div>
                <h2 className="ns-pkot-title">KOT Register</h2>
              </div>
            </div>

            <div className="ns-pkot-top-right">
              <div className="ns-pkot-quicknav-inline">
                <PosModuleTopNav activeKey="kots" />
              </div>

              <div className="ns-pkot-business-chip">
                <Calendar size={14} />
                <span>Business Date</span>
                <strong>{getBusinessDateLabel()}</strong>
              </div>

              <div className="ns-pkot-compact-date-switcher">
                <Button
                  className="ns-pkot-date-btn"
                  onClick={() => changeDate("prev")}
                  aria-label="Previous business date"
                >
                  <ChevronLeft size={16} />
                </Button>

                <div className="ns-pkot-current-date">
                  {singleDate.format("DD MMM YYYY")}
                </div>

                <Button
                  className="ns-pkot-date-btn"
                  onClick={() => changeDate("next")}
                  aria-label="Next business date"
                >
                  <ChevronRight size={16} />
                </Button>

                <Button className="ns-pkot-reset-btn" onClick={resetToBusinessDate}>
                  <RotateCcw size={14} />
                  Reset
                </Button>
              </div>
            </div>
          </div>
        </section>

        <div className="ns-pkot-kpi-grid">
          <div className="ns-pkot-kpi-card warning">
            <span>Pending</span>
            <strong>{statusCount("pending")}</strong>
            <small>Open kitchen orders</small>
          </div>

          <div className="ns-pkot-kpi-card success">
            <span>Completed</span>
            <strong>{statusCount("completed")}</strong>
            <small>Finished KOTs</small>
          </div>

          <div className="ns-pkot-kpi-card danger">
            <span>Cancelled</span>
            <strong>{statusCount("cancelled")}</strong>
            <small>Cancelled kitchen orders</small>
          </div>

          <div className="ns-pkot-kpi-card blue">
            <span>Visible Items</span>
            <strong>{reportStats.totalItems}</strong>
            <small>{reportStats.totalKots} visible KOTs</small>
          </div>

          <div className="ns-pkot-kpi-card slate">
            <span>Aged KOTs</span>
            <strong>{reportStats.aged}</strong>
            <small>Average age {reportStats.averageAge.toFixed(1)} min</small>
          </div>
        </div>

        <section className="ns-pkot-shell">
          <div className="ns-pkot-toolbar">
            <div className="ns-pkot-search">
              <Search size={17} />
              <input
                type="text"
                placeholder="Search KOT no, table/room, waiter, source..."
                value={searchText}
                onChange={(event) => setSearchText(event.target.value)}
              />
              {searchText ? (
                <button type="button" onClick={() => setSearchText("")} aria-label="Clear search">
                  <XCircle size={14} />
                </button>
              ) : null}
            </div>

            <div className="ns-pkot-toolbar-actions">
              <Popover
                open={filterVisible}
                onOpenChange={setFilterVisible}
                placement="bottomRight"
                trigger="click"
                title="Filters"
                content={filterContent}
              >
                <Button className="ns-pkot-icon-btn">
                  <Filter size={15} />
                  Filter
                </Button>
              </Popover>

              <Button
                className="ns-pkot-icon-btn"
                onClick={fetchReport}
                loading={loading}
              >
                <RefreshCw size={15} />
                Refresh
              </Button>

              <Popover
                placement="bottomRight"
                trigger="click"
                content={
                  <div style={{ display: "grid", gap: 8, minWidth: 210 }}>
                    <Button
                      onClick={() =>
                        downloadCSV(
                          exportHeaderList,
                          exportRows,
                          `KOTs_${statusTab}_${singleDate.format("YYYYMMDD")}.csv`
                        )
                      }
                    >
                      <Download size={15} />
                      Download CSV
                    </Button>
                    <Button
                      onClick={() => {
                        setExportData(exportRows);
                        setExportHeaders(exportHeaderList);
                        setPreviewVisible(true);
                      }}
                    >
                      <Printer size={15} />
                      Print / PDF Preview
                    </Button>
                  </div>
                }
              >
                <Button type="primary" className="ns-pkot-icon-btn">
                  <Download size={15} />
                  Download
                </Button>
              </Popover>
            </div>
          </div>

          <div className="ns-pkot-tabs">
            <Tabs
              activeKey={statusTab}
              onChange={setStatusTab}
              items={[
                {
                  key: "pending",
                  label: (
                    <span className="ns-pkot-tab-title">
                      {STATUS_META.pending.icon}
                      Pending
                      <span className="ns-pkot-tab-count">{statusCount("pending")}</span>
                    </span>
                  ),
                },
                {
                  key: "completed",
                  label: (
                    <span className="ns-pkot-tab-title">
                      {STATUS_META.completed.icon}
                      Completed
                      <span className="ns-pkot-tab-count">{statusCount("completed")}</span>
                    </span>
                  ),
                },
                {
                  key: "cancelled",
                  label: (
                    <span className="ns-pkot-tab-title">
                      {STATUS_META.cancelled.icon}
                      Cancelled
                      <span className="ns-pkot-tab-count">{statusCount("cancelled")}</span>
                    </span>
                  ),
                },
                {
                  key: "all",
                  label: (
                    <span className="ns-pkot-tab-title">
                      <Clock size={15} />
                      All
                    </span>
                  ),
                },
              ]}
            />

            {loading ? (
              <div className="ns-pkot-loader">
                <div className="ns-pkot-loader-card">
                  <Spin size="large" />
                  <span>Loading KOT report...</span>
                </div>
              </div>
            ) : filteredKots.length === 0 ? (
              <Empty
                style={{ padding: "56px 0" }}
                description="No KOTs matched the selected date, status, or filters."
              />
            ) : (
              <Table
                columns={columns}
                dataSource={filteredKots}
                rowKey={(record) => record.kot_id || record.kot_number}
                scroll={{ x: 1250, y: 520 }}
                size="small"
                bordered
                className="ns-pkot-table"
                expandable={expandable}
                pagination={{
                  pageSize: 50,
                  showSizeChanger: true,
                  pageSizeOptions: ["25", "50", "100", "200"],
                  showTotal: (total, range) => `${range[0]}–${range[1]} of ${total} KOTs`,
                }}
              />
            )}
          </div>
        </section>

        <KotPreviewDrawer />

        <PrintReportPreview
          visible={previewVisible}
          onClose={() => setPreviewVisible(false)}
          reportTitle={`KOTs (${statusTab}) — ${singleDate.format("DD MMM YYYY")}`}
          propertyName=""
          data={exportData}
          headers={exportHeaders}
          footerData={footerData}
          totalColumns={[]}
          apiEndpoint="pos/transactions/generate-pdf"
          apiUrl={configuredApiBase}
        />
      </div>
    </div>
  );
};

export default PendingKOTsReport;